import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart3, ShoppingCart, Package, Users, TrendingUp, CircleDollarSign, Activity,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

const formatCurrency = (n: number | null | undefined) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    .format(Number(n || 0));

function KpiCard({
  title, value, icon: Icon, loading, borderColor = "border-slate-400",
}: {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  loading?: boolean;
  borderColor?: string;
}) {
  return (
    <div className={`border-l-[3px] ${borderColor} bg-card rounded-r-lg shadow-sm`}>
      <div className="p-3 sm:p-6">
        <p className="text-xs sm:text-sm font-medium text-muted-foreground flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5" />
          {title}
        </p>
        <div className="text-lg sm:text-2xl font-semibold truncate mt-1">
          {loading ? <Skeleton className="h-7 w-24" /> : value}
        </div>
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const { sessionId } = useAuth();

  const overview = useQuery<{
    accountsCount: number; ordersCount: number; openOrdersCount: number;
    productsCount: number; totalRevenue: number; ytdRevenue: number;
  }>({
    queryKey: ["/api/analytics/overview"],
    queryFn: () => api.get(`/analytics/overview`, sessionId),
  });

  const salesTrend = useQuery<Array<{ month: string; revenue: number; invoices: number }>>({
    queryKey: ["/api/analytics/sales-trend"],
    queryFn: () => api.get(`/analytics/sales-trend`, sessionId),
  });

  const topProducts = useQuery<Array<{ productId: string; name: string; code: string | null; quantity: number; revenue: number }>>({
    queryKey: ["/api/analytics/top-products"],
    queryFn: () => api.get(`/analytics/top-products`, sessionId),
  });

  const topCustomers = useQuery<Array<{ accountId: string; name: string; revenue: number; invoices: number }>>({
    queryKey: ["/api/analytics/top-customers"],
    queryFn: () => api.get(`/analytics/top-customers`, sessionId),
  });

  const orderStatus = useQuery<Array<{ status: string; count: number; total: number }>>({
    queryKey: ["/api/analytics/order-status"],
    queryFn: () => api.get(`/analytics/order-status`, sessionId),
  });

  const maxMonth = Math.max(1, ...(salesTrend.data || []).map((m) => m.revenue));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Analytics"
        description="Business intelligence across orders, products, customers, and pipeline"
      />

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-3">
        <KpiCard title="Total Revenue" value={formatCurrency(overview.data?.totalRevenue)} icon={CircleDollarSign}
          loading={overview.isLoading} borderColor="border-blue-500" />
        <KpiCard title="YTD Revenue" value={formatCurrency(overview.data?.ytdRevenue)} icon={TrendingUp}
          loading={overview.isLoading} borderColor="border-emerald-500" />
        <KpiCard title="Accounts" value={overview.data?.accountsCount ?? 0} icon={Users}
          loading={overview.isLoading} />
        <KpiCard title="Total Orders" value={overview.data?.ordersCount ?? 0} icon={ShoppingCart}
          loading={overview.isLoading} />
        <KpiCard title="Open Orders" value={overview.data?.openOrdersCount ?? 0} icon={Activity}
          loading={overview.isLoading} borderColor="border-amber-500" />
        <KpiCard title="Products" value={overview.data?.productsCount ?? 0} icon={Package}
          loading={overview.isLoading} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-4 w-4" /> Monthly Revenue (Last 12 Months)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {salesTrend.isLoading ? (
            <Skeleton className="h-[240px] w-full" />
          ) : !salesTrend.data?.length ? (
            <div className="h-[240px] flex items-center justify-center text-muted-foreground">
              <p className="text-sm">No revenue data yet</p>
            </div>
          ) : (
            <div className="flex items-end gap-2 h-[240px] pt-4">
              {salesTrend.data.map((m) => (
                <div key={m.month} className="flex-1 flex flex-col items-center justify-end gap-1">
                  <div
                    className="w-full bg-primary/80 rounded-t hover:bg-primary"
                    style={{ height: `${Math.max(2, (m.revenue / maxMonth) * 200)}px` }}
                    title={`${m.month}: ${formatCurrency(m.revenue)} (${m.invoices} invoices)`}
                  />
                  <span className="text-[10px] text-muted-foreground">{m.month.slice(5)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top Products</CardTitle>
          </CardHeader>
          <CardContent>
            {topProducts.isLoading ? <Skeleton className="h-[200px] w-full" />
              : !topProducts.data?.length ? (
                <p className="text-sm text-muted-foreground text-center py-6">No sales data yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topProducts.data.map((p) => (
                      <TableRow key={p.productId}>
                        <TableCell>
                          <Link href={`/products/${p.productId}`} className="font-medium hover:underline">
                            {p.name}
                          </Link>
                          {p.code && <div className="text-xs text-muted-foreground font-mono">{p.code}</div>}
                        </TableCell>
                        <TableCell className="text-right">{p.quantity.toLocaleString()}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(p.revenue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top Customers</CardTitle>
          </CardHeader>
          <CardContent>
            {topCustomers.isLoading ? <Skeleton className="h-[200px] w-full" />
              : !topCustomers.data?.length ? (
                <p className="text-sm text-muted-foreground text-center py-6">No revenue data yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead className="text-right">Invoices</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topCustomers.data.map((c) => (
                      <TableRow key={c.accountId}>
                        <TableCell>
                          <Link href={`/accounts/${c.accountId}`} className="font-medium hover:underline">
                            {c.name}
                          </Link>
                        </TableCell>
                        <TableCell className="text-right">{c.invoices}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(c.revenue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Order Status Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          {orderStatus.isLoading ? <Skeleton className="h-[160px] w-full" />
            : !orderStatus.data?.length ? (
              <p className="text-sm text-muted-foreground text-center py-6">No orders yet</p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {orderStatus.data.map((s) => (
                  <div key={s.status} className="border rounded-md p-3 min-w-[160px]">
                    <Badge variant="outline" className="text-xs">{s.status.replace(/_/g, " ")}</Badge>
                    <div className="text-2xl font-semibold mt-1">{s.count}</div>
                    <div className="text-xs text-muted-foreground">{formatCurrency(s.total)}</div>
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
